import type { ArchiveEntry, FollowUp, Profile } from "../types";
import { requireSupabase } from "./supabase";
import type { TeamMemberKey } from "./team";

export interface ArchiveFilters {
  kind: "all" | "case" | "engagement" | "other";
  year: number | null;
  month: number | null;
  species: string;
}

const entrySelect = `
  id, archive_key, case_id, kind, name, description, species, location, people,
  year, month, stage, video_status, make_video, video_editor, status_touched_at, nas_path, last_media_at, updated_at,
  drive_locations(id, entry_id, drive_file_id, folder_name, web_view_link, relative_path, is_primary)
`;

export async function loadProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await requireSupabase()
    .from("profiles")
    .select("id, email, full_name, role")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data as Profile | null;
}

export async function listProfiles(): Promise<Profile[]> {
  const { data, error } = await requireSupabase()
    .from("profiles")
    .select("id, email, full_name, role")
    .order("full_name");
  if (error) throw error;
  return (data ?? []) as Profile[];
}

export async function searchArchive(query: string, filters?: ArchiveFilters): Promise<ArchiveEntry[]> {
  const db = requireSupabase();
  const term = query.trim();
  let request = db.from("archive_entries").select(entrySelect).is("deleted_at", null);
  if (term) {
    request = request.or(
      `case_id.ilike.%${escapePostgrest(term)}%,name.ilike.%${escapePostgrest(term)}%,description.ilike.%${escapePostgrest(term)}%,location.ilike.%${escapePostgrest(term)}%,people.ilike.%${escapePostgrest(term)}%,folder_name.ilike.%${escapePostgrest(term)}%`,
    );
  }
  if (filters?.kind && filters.kind !== "all") request = request.eq("kind", filters.kind);
  if (filters?.year !== null && filters?.year !== undefined) request = request.eq("year", filters.year);
  if (filters?.month !== null && filters?.month !== undefined) request = request.eq("month", filters.month);
  if (filters?.species) request = request.contains("species", [filters.species]);
  const { data, error } = await request.order("year", { ascending: false }).order("month", { ascending: false }).limit(200);
  if (error) throw error;
  return (data ?? []) as ArchiveEntry[];
}

/** Years (>=1) that have entries, with a count each — for the top-level browse. */
export async function archiveYearCounts(): Promise<{ year: number; count: number }[]> {
  const { data, error } = await requireSupabase()
    .from("archive_entries")
    .select("year")
    .is("deleted_at", null)
    .gte("year", 1)
    .limit(5000);
  if (error) throw error;
  const map = new Map<number, number>();
  for (const row of (data ?? []) as { year: number }[]) map.set(row.year, (map.get(row.year) ?? 0) + 1);
  return Array.from(map, ([year, count]) => ({ year, count })).sort((a, b) => b.year - a.year);
}

/** Months (1-12) that have entries in a year, with a count each — for browsing a year. */
export async function archiveMonthCounts(year: number): Promise<{ month: number; count: number }[]> {
  const { data, error } = await requireSupabase()
    .from("archive_entries")
    .select("month")
    .is("deleted_at", null)
    .eq("year", year)
    .gte("month", 1)
    .limit(5000);
  if (error) throw error;
  const map = new Map<number, number>();
  for (const row of (data ?? []) as { month: number }[]) map.set(row.month, (map.get(row.month) ?? 0) + 1);
  return Array.from(map, ([month, count]) => ({ month, count })).sort((a, b) => a.month - b.month);
}

export async function listFollowUps(): Promise<FollowUp[]> {
  const { data, error } = await requireSupabase()
    .from("follow_ups")
    .select(`
      id, entry_id, active, assigned_to, assigned_person, created_by, source, instructions, priority, due_at,
      reminder_interval_days, next_reminder_at, last_reminder_at, completed_at, created_at, updated_at,
      entry:archive_entries(${entrySelect}),
      assignee:profiles!follow_ups_assigned_to_fkey(id, full_name, email)
    `)
    .eq("active", true)
    .order("due_at", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as unknown as FollowUp[];
}

export async function createFollowUp(input: {
  entryId: string;
  caseId: string | null;
  entryName: string;
  userId: string;
  assignedPerson: TeamMemberKey | null;
  instructions: string;
  priority: "normal" | "high" | "urgent";
  dueAt: string | null;
  reminderIntervalDays?: number;
  driveUrl?: string | null;
}): Promise<void> {
  const { data, error } = await requireSupabase().from("follow_ups").upsert(
    {
      entry_id: input.entryId,
      active: true,
      assigned_to: null,
      assigned_person: input.assignedPerson,
      created_by: input.userId,
      source: "portal",
      instructions: input.instructions,
      priority: input.priority,
      reminder_interval_days: input.reminderIntervalDays ?? 5,
      due_at: input.dueAt || null,
      completed_at: null,
      next_reminder_at: input.dueAt || null,
    },
    { onConflict: "entry_id" },
  ).select("id").single();
  if (error) throw error;
  await notifyTelegram({
    action: "saved",
    follow_up_id: data?.id,
    assignee_key: input.assignedPerson,
    case_id: input.caseId,
    entry_name: input.entryName,
    instructions: input.instructions,
    priority: input.priority,
    due_at: input.dueAt,
    drive_url: input.driveUrl ?? null,
  });
}

/** Set a case's video decision (make_video + editor) on the archive entry, via the
 *  worker (service role). Two-way: this flows to the desktop's .meta.json on sync. */
export async function saveCaseVideo(input: {
  entryId: string;
  caseId: string | null;
  entryName: string;
  makeVideo: boolean | null;
  videoEditor: TeamMemberKey | null;
  driveUrl?: string | null;
  notify: boolean;
}): Promise<void> {
  await notifyTelegram({
    __endpoint: "/api/case/video",
    entry_id: input.entryId,
    make_video: input.makeVideo,
    video_editor: input.makeVideo ? input.videoEditor : null,
    case_id: input.caseId,
    entry_name: input.entryName,
    drive_url: input.driveUrl ?? null,
    notify: input.notify,
  });
}

export async function completeFollowUp(item: FollowUp): Promise<void> {
  // Retire the follow-up AND clear the case's ★ flag (+ stamp status_touched_at)
  // so the desktop pulls the completion down and won't re-create it. One RPC.
  const { error } = await requireSupabase().rpc("complete_followup", { p_follow_up_id: item.id });
  if (error) throw error;
  await notifyTelegram({
    action: "completed",
    assignee_key: item.assigned_person,
    case_id: item.entry?.case_id,
    entry_name: item.entry?.name || "Case",
    drive_url: item.entry?.drive_locations?.[0]?.web_view_link ?? null,
  });
}

/** Read the global Telegram on/off switch (fail-open to true). */
export async function getTelegramEnabled(): Promise<boolean> {
  const { data, error } = await requireSupabase().from("app_config").select("telegram_enabled").eq("id", 1).maybeSingle();
  if (error) return true;
  return (data?.telegram_enabled as boolean | undefined) !== false;
}

/** Flip the global Telegram switch (via the worker; service role writes it). */
export async function setTelegramEnabled(enabled: boolean): Promise<void> {
  const { data } = await requireSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/api/config/telegram", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error("Could not update the Telegram setting");
}

async function notifyTelegram(payload: Record<string, unknown>): Promise<void> {
  const { __endpoint, ...rest } = payload as { __endpoint?: string } & Record<string, unknown>;
  const endpoint = typeof __endpoint === "string" ? __endpoint : "/api/telegram/followup";
  try {
    const { data } = await requireSupabase().auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(rest),
    });
  } catch (error) {
    console.warn("Follow-up saved, but Telegram notification could not be sent", error);
  }
}

function escapePostgrest(value: string): string {
  return value.replace(/[,%()]/g, " ").trim();
}
